/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const MediaFrameCtor =
  (typeof globalThis !== "undefined" && (globalThis.MediaFrameNode || globalThis.MediaFrame)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const MEDIA_FRAME_PROPS = ["mediaType", "networkable"];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */

class MediaFrameEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    MEDIA_FRAME_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */

class MediaFrameAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const COMPONENT = "media-frame";

    // Resolve ECS world & entity id (if any)
    this._world = typeof window !== "undefined" ? window.APP?.world : null;
    const findEid = obj => {
      if (!obj) return undefined;
      if (typeof obj.eid === "number" && obj.eid) return obj.eid;
      for (const child of obj.children || []) {
        const eid = findEid(child);
        if (eid !== undefined) return eid;
      }
      return undefined;
    };
    this._eid = findEid(this.sourceObject?.el?.object3D);

    // Obtain ECS helpers
    const ecsAccess = ScriptableObject.mediaFrameEcsAccess || {};
    const { MediaFrame, MediaType, Networked, addComponent, removeComponent, hasComponent } = ecsAccess;

    // Cache for updates made before the component is initialised
    const _pendingUpdates = {};

    const getComponentProp = (component, key) => {
      const attr = this.sourceObject?.el?.getAttribute(component);
      if (attr) return attr[key];
      return _pendingUpdates[key];
    };

    // Helper maps for MediaType
    const MEDIA_TYPE_ENCODING = MediaType
      ? {
          all: MediaType.ALL,
          "all-2d": MediaType.ALL_2D,
          model: MediaType.MODEL,
          image: MediaType.IMAGE,
          video: MediaType.VIDEO,
          pdf: MediaType.PDF
        }
      : null;
    const MEDIA_TYPE_DECODING = MEDIA_TYPE_ENCODING
      ? Object.fromEntries(Object.entries(MEDIA_TYPE_ENCODING).map(([k, v]) => [v, k]))
      : null;

    this._pending = _pendingUpdates;
    this._mediaTypeEncoding = MEDIA_TYPE_ENCODING;

    // mediaType
    Object.defineProperty(this, "mediaType", {
      get: () => {
        if (this._eid != null && MediaFrame && MEDIA_TYPE_DECODING) {
          const val = MediaFrame.mediaType[this._eid];
          if (val !== undefined) return MEDIA_TYPE_DECODING[val] ?? "all";
        }
        return getComponentProp(COMPONENT, "mediaType");
      },
      set: v => {
        if (v == null) return;

        if (this._eid != null && MediaFrame && MEDIA_TYPE_ENCODING) {
          const encoded = MEDIA_TYPE_ENCODING[v] ?? MEDIA_TYPE_ENCODING["all"];
          MediaFrame.mediaType[this._eid] = encoded;
        } else {
          this._pending.mediaType = v;
        }
      }
    });

    // networkable
    Object.defineProperty(this, "networkable", {
      get: () => this._pending.networkable ?? false,
      set: v => {
        const val = !!v;
        this._pending.networkable = val;

        if (this._eid != null && Networked && addComponent && removeComponent && hasComponent && this._world) {
          const has = hasComponent(this._world, Networked, this._eid);
          if (val && !has) addComponent(this._world, Networked, this._eid);
          else if (!val && has) removeComponent(this._world, Networked, this._eid);
        }
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }

  setEid(eid) {
    if (typeof eid === "number") {
      this._eid = eid;

      // Flush pending values
      const ecsAccess = ScriptableObject.mediaFrameEcsAccess || {};
      const { MediaFrame, Networked, addComponent, removeComponent, hasComponent } = ecsAccess;

      if (this._pending.mediaType != null && MediaFrame && this._mediaTypeEncoding) {
        const encoded = this._mediaTypeEncoding[this._pending.mediaType] ?? this._mediaTypeEncoding["all"];
        MediaFrame.mediaType[this._eid] = encoded;
        delete this._pending.mediaType;
      }

      if (
        this._pending.networkable !== undefined &&
        Networked &&
        addComponent &&
        removeComponent &&
        hasComponent &&
        this._world
      ) {
        const has = hasComponent(this._world, Networked, this._eid);
        if (this._pending.networkable && !has) addComponent(this._world, Networked, this._eid);
        else if (!this._pending.networkable && has) removeComponent(this._world, Networked, this._eid);
        delete this._pending.networkable;
      }
    }
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of MEDIA_FRAME_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new MediaFrameAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new MediaFrameEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}

export class MediaFrameElement extends ScriptableObject {
  static scriptableType = "media-frame";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("media-frame", "");

      const fullInitial = { networkable: false, ...initial };

      Object.keys(fullInitial).forEach(key => {
        if (key === "mediaType" && fullInitial[key]) {
          el.setAttribute("media-frame", { mediaType: fullInitial[key] });
        }
      });

      if (fullInitial.networkable) {
        // Set up networked component if needed
      }

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      // Initialize ECS if available
      let _eidCreated;
      if (typeof ScriptableObject.mediaFrameEcsInit === "function") {
        try {
          _eidCreated = ScriptableObject.mediaFrameEcsInit(el, fullInitial);
        } catch (e) {
          console.warn("MediaFrameElement: Failed to initialise ECS entity", e);
        }
      }

      if (_eidCreated != null && obj._adapter?.setEid) {
        obj._adapter.setEid(_eidCreated);
      }

      // Manually add the guide object if it was created by ECS init
      if (_eidCreated != null && typeof window !== "undefined" && window.APP?.world) {
        const world = window.APP.world;
        // Access ECS components to get the guide
        const ecsAccess = ScriptableObject.mediaFrameEcsAccess || {};
        const { MediaFrame } = ecsAccess;
        if (MediaFrame && MediaFrame.guide && world.eid2obj) {
          const guideEid = MediaFrame.guide[_eidCreated];
          if (guideEid != null) {
            const guideObj = world.eid2obj.get(guideEid);
            if (guideObj && !obj.children.includes(guideObj)) {
              // Remove the artificial parent and add to MediaFrameElement
              if (guideObj.parent && guideObj.parent !== obj) {
                guideObj.parent.remove(guideObj);
              }
              obj.add(guideObj);
            }
          }
        }
      }

      obj.updateMatrixWorld(true);

      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createMediaFrameElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public media frame props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of MEDIA_FRAME_PROPS) {
      take(p, source[p]);
    }

    // 3) Don't clear existing children on aframe context - let the new MediaFrameElement keep its ECS-created entities
    // This ensures functionality (clickable, networked, physics) is preserved
    if (!HAS_AFRAME) {
      // Clear existing children
      for (const c of [...this.children]) {
        this.remove(c);
      }
    }

    // 4) Copy children from source
    // WARNING: This will create duplicate infrastructure children (guides, ECS entities)
    // Each MediaFrameElement needs its own ECS infrastructure to work properly (networking, physics, interaction)
    // The duplication is intentional because we don't have a reliable way to identify the children that are ECS-created - each clone gets both:
    //   - Its own functional ECS infrastructure (created during construction)
    //   - Copies of all source children (including source's infrastructure)
    // This ensures the clone works properly even though it has some duplicate infrastructure objects.
    if (recursive) {
      const sourceChildren = logicalChildrenOf(source);

      for (const srcChild of sourceChildren) {
        const clonedChild = srcChild.clone(true);
        this.add(clonedChild);
      }

      // Ensure matrix world is updated after adding children
      this.updateMatrixWorld(true);
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of MEDIA_FRAME_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Create a new MediaFrameElement with full ECS initialization (so it works properly)
    const Ctor =
      this.constructor && typeof this.constructor.create === "function" ? this.constructor : MediaFrameElement;
    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new MediaFrameElement(init);

    // Write object3D transforms (copy position, rotation, scale before copying children)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Update matrix world before copying children
    dest.updateMatrixWorld(true);

    // Copy remaining fields & children - but this will REPLACE the newly created children with cloned ones
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor MediaFrameNode instance to Scriptable, attach adapter and proxies
function makeEditorMediaFrameNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin(
    (originalProto && originalProto.constructor) || MediaFrameCtor || THREE.Object3D
  );
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType)
      Object.defineProperty(node, "scriptableType", { value: "media-frame", configurable: true });
  } catch (_) {
    node.scriptableType = "media-frame";
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", {
      value: new MediaFrameEditorAdapter(node, descriptors),
      configurable: true
    });
  } catch (_) {
    node._adapter = new MediaFrameEditorAdapter(node, descriptors);
  }

  for (const prop of MEDIA_FRAME_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

export function createMediaFrameElementEditor(initial = {}) {
  const MediaFrameCtor = globalThis.MediaFrameNode;

  if (typeof MediaFrameCtor !== "function") {
    throw new Error("MediaFrameElementEditor: MediaFrameNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new MediaFrameCtor(editor);
  } catch (e) {
    throw new Error(`MediaFrameElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorMediaFrameNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

ScriptableObjects.registerType("media-frame", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== MediaFrameElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return MediaFrameElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorMediaFrameNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(MediaFrameElement.prototype, Symbol.toStringTag, { value: "MediaFrameElement" });

export { MediaFrameAFrameAdapter, MediaFrameEditorAdapter };
